import React, { useState, useEffect } from 'react';
import {
  User,
  Phone,
  Calendar,
  MapPin,
  Lock,
  Mail,
  Stethoscope,
  GraduationCap,
  Building2,
  ArrowLeft,
  Edit,
  Trash2,
  UserPlus, Settings,
  FileText

} from 'lucide-react';
import { fetchAllData, fetchDeleteData } from '../../helpers/externapi';
import Layout from '../../Layout/Layout';
import PageWrapper from '../../Components/PageWrapper';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Chip, Divider, Tooltip } from '@mui/material';
import ConfirmationDialogDelete from '../../Components/ConfirmationDialogDelete';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { fetchConfigValues } from '../../Commoncomponents/CommonComponents';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ClinicUserDetails() {
  const { smartClinicId, userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [documentImageUrl, setDocumentImageUrl] = useState('');


  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        // Fetch config for image URLs
        const { userImagesBucketlink, userDocumentsBucketURL } = await fetchConfigValues();
        setImageUrl(userImagesBucketlink || '');
        setDocumentImageUrl(userDocumentsBucketURL || '');

        // Fetch user data
        const response = await fetchAllData(`lambdaAPI/User/GetById/${userId}`);
        if (response) {
          setUser(response[0]);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setSnackbarMessage("Failed to load user data");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  const handleDelete = () => {
    setConfirmationOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetchDeleteData(`lambdaAPI/User/delete/${userId}`);
      if (response.status === false) {
        setSnackbarMessage(response.message);
        setSnackbarOpen(true);
        return;
      }
      setSnackbarMessage("User deleted successfully");
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate(`/smartclinics/details/${smartClinicId}`);
      }, 1500);
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbarMessage("Failed to delete user");
      setSnackbarOpen(true);
    }
  };

  const handleEdit = () => {
    navigate(`/smart-clinics/${smartClinicId}/edit-user/${userId}`);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getRoleIcon = (roleId) => {
    switch (roleId) {
      case 1: return <Stethoscope style={{ width: '20px', height: '20px' }} />;
      case 2: return <User style={{ width: '20px', height: '20px' }} />;
      case 3: return <Building2 style={{ width: '20px', height: '20px' }} />;
      case 4: return <GraduationCap style={{ width: '20px', height: '20px' }} />;
      case 34: return <UserPlus style={{ width: '20px', height: '20px' }} />; // For Auxiliary Nurse
      case 35: return <Settings style={{ width: '20px', height: '20px' }} />; // For Smart Clinic Admin
      default: return <User style={{ width: '20px', height: '20px' }} />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageWrapper>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </PageWrapper>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <PageWrapper>
          <div className="text-center py-5">
            <p className="text-danger">User not found</p>
            <button
              onClick={() => navigate(`/smartclinics/details/${smartClinicId}`)}
              className="btn btn-primary mt-3"
            >
              Back to Clinic
            </button>
          </div>
        </PageWrapper>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageWrapper>
        <div className="container" style={{ maxWidth: '1200px', padding: '20px' }}>
          {/* Header Section */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button
              onClick={() => navigate(`/smartclinics/details/${smartClinicId}`)}
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
            >
              <ArrowLeft style={{ width: '20px', height: '20px' }} />
              Back to Clinic
            </button>

            <div className="d-flex gap-2">
              <Tooltip title="Edit User">
                <Button
                  variant="outlined"
                  startIcon={<Edit size={18} />}
                  onClick={handleEdit}
                >
                  Edit
                </Button>
              </Tooltip>
              <Tooltip title="Delete User">
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Trash2 size={18} />}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </Tooltip>
            </div>
          </div>

          {/* User Profile Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row">
                {/* Profile Image */}
                <div className="col-md-3 mb-4 mb-md-0">
                  <div className="d-flex justify-content-center">
                    <div className="position-relative">
                      <div
                        className="rounded border"
                        style={{
                          width: '160px',
                          height: '160px',
                          overflow: 'hidden',
                          backgroundColor: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {user.UserImage ? (
                          <img
                            src={`${imageUrl}${user.UserImage}`}
                            alt={user.FullName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <User style={{ width: '80px', height: '80px', color: '#6c757d' }} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* User Info */}
                <div className="col-md-9">
                  <div className="d-flex align-items-center mb-3">
                    <h1 className="h3 mb-0 me-3">{user.FullName}</h1>
                    <Chip
                      label={user.IsActive ? "Active" : "Inactive"}
                      color={user.IsActive ? "success" : "error"}
                      size="small"
                    />
                  </div>

                  {/* Role */}
                  {user.UserRoleId && (
                    <div className="d-flex align-items-center mb-3">
                      {getRoleIcon(user.UserRoleId)}
                      <span className="ms-2 text-muted">
                        {user.UserRoleName || 'Role not specified'}
                      </span>
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="row mb-3">
                    <div className="col-md-6 mb-2">
                      <div className="d-flex align-items-center">
                        <Phone style={{ width: '20px', height: '20px', color: '#6c757d', marginRight: '10px' }} />
                        <span>{user.MobileNumber || 'Not provided'}</span>
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <div className="d-flex align-items-center">
                        <Mail style={{ width: '20px', height: '20px', color: '#6c757d', marginRight: '10px' }} />
                        <span>{user.EmailId || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Personal Details */}
                  <div className="row">
                    <div className="col-6 col-md-4 mb-3">
                      <div className="bg-light p-3 rounded">
                        <p className="small text-muted mb-1">Gender</p>
                        <p className="mb-0">{user.Gender || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="col-6 col-md-4 mb-3">
                      <div className="bg-light p-3 rounded">
                        <p className="small text-muted mb-1">Date of Birth</p>
                        <p className="mb-0">
                          {user.DateofBirth ? new Date(user.DateofBirth).toLocaleDateString() : 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <div className="col-6 col-md-4 mb-3">
                      <div className="bg-light p-3 rounded">
                        <p className="small text-muted mb-1">Age</p>
                        <p className="mb-0">{user.Age || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="col-6 col-md-4 mb-3">
                      <div className="bg-light p-3 rounded">
                        <p className="small text-muted mb-1">Qualification</p>
                        <p className="mb-0">{user.Qualification || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="col-6 col-md-4 mb-3">
                      <div className="bg-light p-3 rounded">
                        <p className="small text-muted mb-1">Clinic ID</p>
                        <p className="mb-0">{user.SmartClinicId || 'Not assigned'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            {/* Address Section */}
            <div className="card-body p-4">
              <h2 className="h5 d-flex align-items-center mb-4">
                <MapPin style={{ width: '20px', height: '20px', color: '#0d6efd', marginRight: '10px' }} />
                Address Information
              </h2>

              <div className="row">
                {/* Address Details */}
                <div className="col-md-6 mb-4 mb-md-0">
                  <div className="mb-4">
                    <div className="bg-light p-3 rounded">
                      <h3 className="small text-muted mb-2">Primary Address</h3>
                      <p className="mb-0">
                        {[user.AddressLine1, user.AddressLine2].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6 mb-3">
                      <div className="bg-light p-3 rounded">
                        <p className="small text-muted mb-1">Village</p>
                        <p className="mb-0">{user.Village || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="col-6 mb-3">
                      <div className="bg-light p-3 rounded">
                        <p className="small text-muted mb-1">Mandal</p>
                        <p className="mb-0">{user.Mandal || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="col-6 mb-3">
                      <div className="bg-light p-3 rounded">
                        <p className="small text-muted mb-1">City</p>
                        <p className="mb-0">{user.City || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="col-6 mb-3">
                      <div className="bg-light p-3 rounded">
                        <p className="small text-muted mb-1">Pincode</p>
                        <p className="mb-0">{user.Pincode || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Details */}
                <div className="col-md-6">
                  <div className="mb-4">
                    <div className="bg-light p-3 rounded">
                      <h3 className="small text-muted mb-2">Location</h3>
                      <div className="row">
                        <div className="col-6 mb-2">
                          <p className="small text-muted mb-1">State</p>
                          <p className="mb-0">{user.StateName || 'Not specified'}</p>
                        </div>
                        <div className="col-6 mb-2">
                          <p className="small text-muted mb-1">District</p>
                          <p className="mb-0">{user.DistrictName || 'Not specified'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            {/* Document Section */}
            <div className="card-body p-4">
              <h2 className="h5 d-flex align-items-center mb-4">
                <FileText style={{ width: '20px', height: '20px', color: '#0d6efd', marginRight: '10px' }} />
                Document Information
              </h2>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="bg-light p-3 rounded">
                    <p className="small text-muted mb-1">Document Type</p>
                    <p className="mb-0">{user.DocumentType || 'Not specified'}</p>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="bg-light p-3 rounded">
                    <p className="small text-muted mb-1">Document</p>
                    {user.DocumentImage ? (
                      <a
                        href={`${documentImageUrl}${user.DocumentImage}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        View Document
                      </a>
                    ) : (
                      <p className="mb-0">No document uploaded</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Dialog */}
        <ConfirmationDialogDelete
          open={confirmationOpen}
          title="Delete User"
          message="Are you sure you want to delete this user? This action cannot be undone."
          onConfirm={confirmDelete}
          onCancel={() => setConfirmationOpen(false)}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </PageWrapper>
    </Layout>
  );
}